import { useTranslate } from "hooks/useTranslate"
import { Children, PropsWithChildren, ReactNode } from "react"

import { IntlKey, isIntlKey } from "@shared/intl"
import { TranslationEntry } from "@shared/translations/translationEntry"

/**
 * A type that can be used to pass translatable content to the component.
 * The value can be either a translation entry (preferred) or a translation key (discouraged).
 * Components using TranslatableText can use this type to describe their properties
 * that would be passed into the TranslatableText component.
 */
export type TranslatableProp = IntlKey | TranslationEntry

/**
 * Type for react-intl's rich text formatting approach (allowing specific HTML
 * attributes in translations)
 */
type RichTextValues = (chunks: ReactNode[]) => ReactNode

/**
 *  An internal interface to describe a translatable "t" property.
 */
interface TranslatableTProp {
  /**
   * The translation key that should be provided to the component
   * to get proper translation.
   *
   * Replaces `children`: when both are set the translation is rendered and the
   * children never reach the DOM. Setting both throws in development.
   */
  t?: TranslatableProp

  /**
   * Optional values that can be used for interpolation in the translation strings.
   */
  tValues?: Record<string, string | number | boolean | ReactNode | RichTextValues>
}

/**
 *  The public interface for the translatable text component. Pass either "t" — a
 *  translation key or entry, rendered through the i18n registry — or "children",
 *  never both: "t" replaces "children", so passing both silently drops the children
 *  and throws in development. See {@link assertNoConflictingTextProps}.
 */
export interface TranslatableTextProps extends TranslatableTProp, PropsWithChildren {
  /**
   * The content to render when no translation key is given.
   *
   * Replaced by `t`: when both are set only the translation is rendered.
   * Setting both throws in development.
   */
  children?: ReactNode
}

/**
 *  This is a simple component that is bundled with an interface that can be used
 *  to present translatable text or display children. Extending components would
 *  extend from this component interface to provide correct props types and then
 *  internally use this component to present the translatable text or display children.
 *
 *  ```ts
 *  import { TranslatableText, TranslatableTextProps } from "components/TranslatableText/TranslatableText"
 *
 *  export interface TextComponentProps extends TranslatableTextProps {
 *     color: TextColor
 *  }
 *
 *  export function TextComponent({ color, ...props }: TextComponentProps) {

 *     return <span className={color}>
 *       <TranslatableText {...props} />
 *     </span>
 *  }
 *  ```
 */
export function TranslatableText(props: TranslatableTextProps) {
  const translate = useTranslate()
  assertNoConflictingTextProps(props)

  if (isTranslatableTProp(props) && props.t !== undefined) {
    return translate(props.t, props.tValues)
  }

  if (isPropsWithChildren(props) && props.children !== undefined) {
    return props.children
  }

  return null
}

/**
 * Refuses the ambiguous combination of a "t" prop and children: "t" wins and the children
 * never reach the DOM, which is silent at the call site — an `<input>` nested in a
 * `<Button t="…">` once made an attach-file button do nothing in production.
 *
 * Development only. In production the check is a no-op, so "t" keeps winning rather than
 * taking down a running page over a rendering mistake.
 */
function assertNoConflictingTextProps({ t, children }: TranslatableTextProps): void {
  if (!import.meta.env.DEV || t === undefined || Children.toArray(children).length === 0) {
    return
  }

  throw new Error(
    `TranslatableText was given both a "t" prop (${describeTranslatableProp(t)}) and "children". ` +
      `The "t" prop replaces "children", so the children would never be rendered. Pass only one of them.`
  )
}

// Describes a "t" value for the error message above, without assuming it is a plain key.
function describeTranslatableProp(t: TranslatableProp): string {
  return t instanceof TranslationEntry ? `TranslationEntry ${JSON.stringify(t.key)}` : `"${t}"`
}

// a helper function to detect if we are dealing with a translatable "t" property
function isTranslatableTProp(props: TranslatableTextProps): props is TranslatableTProp {
  return "t" in props && props.t !== undefined
}

// a helper function to detect if we are dealing with children props
function isPropsWithChildren(props: TranslatableTextProps): props is PropsWithChildren {
  return "children" in props && props.children !== undefined
}

/**
 * A helper function to detect if a prop is a translatable prop.
 */
export function isTranslatableProp(prop: unknown): prop is TranslatableProp {
  return prop instanceof TranslationEntry || (typeof prop === "string" && isIntlKey(prop))
}
